import { Fragment } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { PanelHeader } from '@/components/ui/panel-header'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useBreadcrumbStore } from '@/stores/useBreadcrumbStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useMyBusinessStore } from '@/stores/useMyBusinessStore'
import { BackToHome } from '@/components/layout/BackToHome'
import { HeaderActions } from '@/components/layout/HeaderActions'
import { ChatPanelButton } from '@/components/layout/ChatPanel'
import { generateBreadcrumbs } from '@/components/layout/breadcrumbs'

/** Below md the panel isn't on screen, so every page names the business it shows; the
 * business is switched from the Agents tree on home. */
function ActiveBusinessName() {
  const activeId = useAuthStore((state) => state.user?.default_business_id)
  const businesses = useMyBusinessStore((state) => state.businesses)
  const active = businesses.find((b) => b.id === activeId) ?? businesses[0]
  return (
    <p
      className="text-muted-foreground truncate px-4 pb-2 text-xs md:hidden"
      data-testid="active-business-name"
    >
      {active?.name ?? '\u00a0'}
    </p>
  )
}

/** Page top bar. `/chat` renders its own bar (ChatSurface), so this never mounts there. Its
 * Marketing button opens the chat beside the page. */
export function Header() {
  const location = useLocation()
  const breadcrumbLabels = useBreadcrumbStore((state) => state.labels)
  const crumbs = generateBreadcrumbs(location.pathname, breadcrumbLabels)

  return (
    <header data-testid="header" className="glass border-border/60 border-b">
      <PanelHeader
        className="border-b-0"
        leading={
          // A Settings page goes back to the Settings list; any other screen to home.
          <BackToHome to={location.pathname.startsWith('/settings/') ? '/settings' : undefined} />
        }
        title={
          <Breadcrumb className="min-w-0 overflow-hidden">
            <BreadcrumbList className="flex-nowrap">
              {crumbs.map((crumb, index) => (
                <Fragment key={crumb.path}>
                  {index > 0 && <BreadcrumbSeparator className="hidden lg:block" />}
                  <BreadcrumbItem
                    className={crumb.isLast ? 'min-w-0' : 'hidden min-w-0 lg:inline-flex'}
                  >
                    {crumb.isLast ? (
                      <BreadcrumbPage className="truncate text-base font-semibold">
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild className="truncate">
                        <Link to={crumb.path}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        }
        actions={
          <>
            <HeaderActions />
            <ChatPanelButton />
          </>
        }
      />

      <ActiveBusinessName />
    </header>
  )
}
